import React, { useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { createFAQ, updateFAQ, deleteFAQ } from '../../utils/api';

const faqCategories = ['General', 'Bookings', 'Products', 'Forum', 'Legal', 'Account'];

export const AdminFAQs = ({ cmsFaqs, loadData }) => {
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({ category: 'General', question: '', answer: '', display_order: 0, is_published: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaq) { await updateFAQ(editingFaq.id, faqForm); toast.success('FAQ updated'); }
      else { await createFAQ(faqForm); toast.success('FAQ created'); }
      setFaqForm({ category: 'General', question: '', answer: '', display_order: 0, is_published: true });
      setEditingFaq(null); loadData();
    } catch (err) { toast.error('Failed to save FAQ'); }
  };

  return (
    <div className="space-y-6" data-testid="faqs-cms-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4">{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category *</Label><select value={faqForm.category} onChange={(e) => setFaqForm({...faqForm, category: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2" required>{faqCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></div>
              <div><Label>Display Order</Label><Input type="number" value={faqForm.display_order} onChange={(e) => setFaqForm({...faqForm, display_order: parseInt(e.target.value) || 0})} /></div>
            </div>
            <div><Label>Question *</Label><Input value={faqForm.question} onChange={(e) => setFaqForm({...faqForm, question: e.target.value})} placeholder="Enter the question..." required /></div>
            <div><Label>Answer *</Label><Textarea value={faqForm.answer} onChange={(e) => setFaqForm({...faqForm, answer: e.target.value})} placeholder="Enter the answer..." rows={4} required /></div>
            <div className="flex items-center gap-2"><input type="checkbox" checked={faqForm.is_published} onChange={(e) => setFaqForm({...faqForm, is_published: e.target.checked})} className="rounded" /><Label>Published</Label></div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-[#8B1818] text-white">{editingFaq ? 'Update FAQ' : 'Create FAQ'}</Button>
              {editingFaq && <Button type="button" variant="outline" onClick={() => { setEditingFaq(null); setFaqForm({ category: 'General', question: '', answer: '', display_order: 0, is_published: true }); }}>Cancel</Button>}
            </div>
          </form>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Existing FAQs ({cmsFaqs.length})</h2>
          {cmsFaqs.length === 0 ? <p className="text-slate-500 text-center py-8">No FAQs yet.</p> : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {faqCategories.map(category => {
                const categoryFaqs = cmsFaqs.filter(f => f.category === category);
                if (categoryFaqs.length === 0) return null;
                return (
                  <div key={category}>
                    <h4 className="font-semibold text-slate-700 mb-2 text-sm uppercase tracking-wide">{category}</h4>
                    {categoryFaqs.map((faq) => (
                      <div key={faq.id} className="border border-slate-200 rounded-lg p-3 mb-2 flex justify-between items-start">
                        <div className="flex-1"><p className="font-medium text-slate-900 text-sm">{faq.question}</p><p className="text-slate-500 text-xs mt-1 truncate">{faq.answer.substring(0, 100)}...</p>{!faq.is_published && <span className="text-xs text-yellow-600">(Draft)</span>}</div>
                        <div className="flex gap-1 ml-2">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingFaq(faq); setFaqForm({ category: faq.category, question: faq.question, answer: faq.answer, display_order: faq.display_order || 0, is_published: faq.is_published !== false }); }}><Edit className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => { if (window.confirm('Delete?')) { await deleteFAQ(faq.id); toast.success('Deleted'); loadData(); } }}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
